/**
 * uiModeStore
 *
 * Controls whether the UI shows the full technical surface (validator
 * findings, quarantined rules, extraction provenance, rule IDs) or the
 * stripped-down end-user surface (cited code paragraph, observed vs
 * threshold, what Vera could not decide).
 *
 * Defaults to "technical" so a freshly-mounted demo session shows the
 * pitch-day surface (architectural credibility, ontology story,
 * human-in-the-loop). Flip to "end-user" when demoing the customer
 * experience.
 *
 * Persisted to localStorage so a reload doesn't reset the user back to
 * technical mid-presentation.
 */
import { create } from "zustand";

export type UiMode = "technical" | "end-user";

const STORAGE_KEY = "vera.uiMode";

function loadInitialMode(): UiMode {
  if (typeof window === "undefined") return "technical";
  const raw = window.localStorage.getItem(STORAGE_KEY);
  return raw === "end-user" ? "end-user" : "technical";
}

interface UiModeStore {
  mode: UiMode;
  setMode: (mode: UiMode) => void;
  toggle: () => void;
}

export const useUiModeStore = create<UiModeStore>(set => ({
  mode: loadInitialMode(),
  setMode: mode => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem(STORAGE_KEY, mode);
    }
    set({ mode });
  },
  toggle: () =>
    set(s => {
      const next: UiMode = s.mode === "technical" ? "end-user" : "technical";
      if (typeof window !== "undefined") {
        window.localStorage.setItem(STORAGE_KEY, next);
      }
      return { mode: next };
    }),
}));

/** Convenience selector. */
export function isTechnicalMode(s: { mode: UiMode }): boolean {
  return s.mode === "technical";
}
